<template>
  <div
    class="lead-card"
    id="lead-funnel"
  >
    <div class="lk-ring">
      <div
        class="lk-card"
        data-lk
      >
        <div class="lk-top">
          <div
            class="lk-pearls"
            aria-hidden="true"
          >
            <i
              v-for="item in pearls"
              :key="item.key"
              :class="[item.kind, { done: item.done, current: item.current }]"
            ></i>
          </div>
          <span
            class="lk-count"
            aria-live="polite"
          >{{ countText }}</span>
        </div>
        <p
          v-show="showResume"
          class="lk-resume"
        >
          Weiter, wo du warst ·
          <button
            type="button"
            class="lk-restart"
            @click="restart"
          >
            Neu starten
          </button>
        </p>

        <div class="lk-body">
          <div
            class="lk-crumbs"
            :class="{ has: crumbs.length }"
          >
            <button
              v-for="c in crumbs"
              :key="c.step"
              type="button"
              :class="{ 'is-new': c.step === newestCrumbStep }"
              :aria-label="'Zurück zu Schritt ' + c.step + ': ' + c.title"
              @click="goTo(c.step)"
            >
              {{ c.label }}
            </button>
          </div>
          <p
            class="sr-only"
            aria-live="polite"
            id="lk-live"
          >
            {{ liveText }}
          </p>

          <div
            ref="viewport"
            class="lk-viewport"
            :class="{ 'is-animating': viewportAnimating }"
            :style="viewportHeight ? { height: viewportHeight } : null"
          >
            <LeadFunnelStepGender
              ref="step1"
              :class="stepClass(1)"
              :gender="form.gender"
              :preferred-gender="form.preferredGender"
              @update:gender="onGenderUpdate"
              @update:preferredGender="onPreferredGenderUpdate"
            />

            <LeadFunnelStepAgeRange
              ref="step2"
              :class="stepClass(2)"
              :age-min="form.ageMin"
              :age-max="form.ageMax"
              @update:ageMin="onAgeMinUpdate"
              @update:ageMax="onAgeMaxUpdate"
            />

            <LeadFunnelStepPreferences
              ref="step3"
              :class="stepClass(3)"
              :preferences="form.preferences"
              @update:preferences="onPreferencesUpdate"
            />

            <LeadFunnelStepBirthday
              ref="step4"
              :class="stepClass(4)"
              :day="form.day"
              :month="form.month"
              :year="form.year"
              @update:day="onDobFieldUpdate('day', $event)"
              @update:month="onDobFieldUpdate('month', $event)"
              @update:year="onDobFieldUpdate('year', $event)"
            />

            <LeadFunnelStepLocation
              ref="step5"
              :class="stepClass(5)"
              :location-id="form.locationId"
              :location-label="form.locationLabel"
              @update:locationId="onLocationIdUpdate"
              @update:locationLabel="onLocationLabelUpdate"
              @picked="onLocationPicked"
            />

            <LeadFunnelStepSignup
              ref="step6"
              :class="stepClass(6)"
              :name="form.name"
              :email="form.email"
              :password="form.password"
              :terms="form.terms"
              :can-submit="step6Valid"
              :submitting="submitting"
              :submit-error="submitError"
              @update:name="onNameUpdate"
              @update:email="onEmailUpdate"
              @update:password="form.password = $event"
              @update:terms="form.terms = $event"
              @social="goSocial"
              @submit="submit"
            />
          </div>

          <div
            class="lk-foot"
            :class="{ on: footOn, free: footFree }"
          >
            <button
              type="button"
              class="sb-back"
              @click="goTo(current - 1)"
            >
              ← Zurück
            </button>
            <button
              type="button"
              class="sb-next"
              :disabled="!currentValid"
              @click="goNext"
            >
              Weiter <span>→</span>
            </button>
          </div>
        </div>

        <div
          class="lk-loading"
          :hidden="!loadingVisible"
          :class="{ on: loadingOn }"
        >
          <span
            class="lk-heart"
            aria-hidden="true"
          >
            <svg
              viewBox="0 0 24 24"
              fill="currentColor"
            >
              <use href="#viluu-heart" />
            </svg>
          </span>
          <p class="lk-load-txt">{{ loadTxt }}</p>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { oauthUrl, refreshCsrfToken } from '../signup/helpers';
import {
  DRAFT_KEY,
  DRAFT_TTL,
  LOADER_MSGS,
  STEP_TITLES,
  TOTAL_STEPS,
  consentAllowsDraft,
  crumbFor,
  emptyForm,
  firstIncompleteStep,
  homeUrl,
  isStepValid,
  pad2,
} from './kennenlernenLeadFunnel';
import LeadFunnelStepGender from './steps/LeadFunnelStepGender.vue';
import LeadFunnelStepAgeRange from './steps/LeadFunnelStepAgeRange.vue';
import LeadFunnelStepPreferences from './steps/LeadFunnelStepPreferences.vue';
import LeadFunnelStepBirthday from './steps/LeadFunnelStepBirthday.vue';
import LeadFunnelStepLocation from './steps/LeadFunnelStepLocation.vue';
import LeadFunnelStepSignup from './steps/LeadFunnelStepSignup.vue';

export default {
  name: 'KennenlernenLeadFunnel',
  components: {
    LeadFunnelStepGender,
    LeadFunnelStepAgeRange,
    LeadFunnelStepPreferences,
    LeadFunnelStepBirthday,
    LeadFunnelStepLocation,
    LeadFunnelStepSignup,
  },
  data() {
    return {
      form: emptyForm(),
      current: 1,
      animating: false,
      pendingGoTo: null,
      measureStep: null,
      ghostStep: null,
      enterDir: null,
      goneDir: null,
      viewportHeight: '',
      viewportAnimating: false,
      footFree: false,
      newestCrumbStep: null,
      showResume: false,
      reduced: false,
      submitting: false,
      submitError: '',
      loadingVisible: false,
      loadingOn: false,
      loadTxt: LOADER_MSGS[0],
      advanceTimer: null,
      draftTimer: null,
      loaderTimer: null,
      goToTimer: null,
      locationAdvanceTimer: null,
      hideLoaderTimer: null,
    };
  },
  computed: {
    countText() {
      return this.current === 1 ? 'Dauert ca. 1 Minute' : 'Schritt ' + this.current + ' von ' + TOTAL_STEPS;
    },
    liveText() {
      return 'Schritt ' + this.current + ' von ' + TOTAL_STEPS + ' — ' + STEP_TITLES[this.current - 1];
    },
    pearls() {
      const items = [];
      for (let i = 1; i <= TOTAL_STEPS; i++) {
        items.push({
          key: 'p' + i,
          kind: 'p',
          done: i < this.current,
          current: i === this.current,
        });
        if (i < TOTAL_STEPS) {
          items.push({
            key: 's' + i,
            kind: 's',
            done: i < this.current,
            current: false,
          });
        }
      }
      return items;
    },
    crumbs() {
      const list = [];
      for (let s = 1; s < this.current; s++) {
        const label = crumbFor(s, this.form, this.trans);
        if (label) {
          list.push({ step: s, label, title: STEP_TITLES[s - 1] });
        }
      }
      return list;
    },
    footOn() {
      return this.current >= 2 && this.current <= 5;
    },
    currentValid() {
      return isStepValid(this.current, this.form);
    },
    step6Valid() {
      return isStepValid(6, this.form);
    },
  },
  watch: {
    crumbs(list, prev) {
      const prevLen = prev ? prev.length : 0;
      if (list.length > prevLen && list.length) {
        this.newestCrumbStep = list[list.length - 1].step;
      }
    },
  },
  created() {
    this.reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  },
  mounted() {
    window.addEventListener('viluu:consent', this.onConsent);
    this.restoreDraft();
  },
  beforeDestroy() {
    window.removeEventListener('viluu:consent', this.onConsent);
    clearTimeout(this.advanceTimer);
    clearTimeout(this.draftTimer);
    clearTimeout(this.goToTimer);
    clearTimeout(this.locationAdvanceTimer);
    clearTimeout(this.hideLoaderTimer);
    clearInterval(this.loaderTimer);
  },
  methods: {
    stepClass(n) {
      return {
        active: this.current === n,
        measure: this.measureStep === n,
        ghost: this.ghostStep === n,
        'enter-r': this.enterDir === 'r' && this.current === n,
        'enter-l': this.enterDir === 'l' && this.current === n,
        'gone-l': this.goneDir === 'l' && this.ghostStep === n,
        'gone-r': this.goneDir === 'r' && this.ghostStep === n,
      };
    },
    stepEl(n) {
      const r = this.$refs['step' + n];
      return r ? r.$el || r : null;
    },
    focusStep(n) {
      const step = this.stepEl(n);
      if (!step) {
        return;
      }
      const target =
        step.querySelector('.lk-q[tabindex]') || step.querySelector('button.pill, input, button.lk-chip');
      if (target) {
        try {
          target.focus({ preventScroll: true });
        } catch (e) {
          /* ignore */
        }
      }
    },
    goNext() {
      if (isStepValid(this.current, this.form)) {
        this.goTo(this.current + 1);
      }
    },
    async goTo(n, opts) {
      if (n < 1 || n > TOTAL_STEPS || n === this.current) {
        return;
      }
      if (this.animating) {
        this.pendingGoTo = { n, opts };
        return;
      }
      const from = this.current;
      const fwd = n > from;
      const instant = this.reduced || (opts && opts.instant);

      if (instant) {
        this.current = n;
        this.ghostStep = null;
        this.enterDir = null;
        this.goneDir = null;
        this.footFree = n >= 2 && n <= 5;
        this.saveDraft();
        if (!(opts && opts.silent)) {
          this.$nextTick(() => this.focusStep(n));
        }
        return;
      }

      this.animating = true;
      const h1 = this.$refs.viewport.offsetHeight;
      this.measureStep = n;
      await this.$nextTick();
      const h2 = this.stepEl(n).offsetHeight;
      this.measureStep = null;
      this.viewportHeight = h1 + 'px';
      this.viewportAnimating = true;
      this.current = n;
      this.ghostStep = from;
      this.enterDir = fwd ? 'r' : 'l';
      this.footFree = false;
      this.saveDraft();
      await this.$nextTick();
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          this.viewportHeight = h2 + 'px';
          this.enterDir = null;
          this.goneDir = fwd ? 'l' : 'r';
        });
      });
      this.goToTimer = setTimeout(() => {
        this.ghostStep = null;
        this.goneDir = null;
        this.viewportAnimating = false;
        this.viewportHeight = '';
        this.footFree = this.current >= 2 && this.current <= 5;
        this.animating = false;
        if (this.pendingGoTo) {
          const p = this.pendingGoTo;
          this.pendingGoTo = null;
          this.goTo(p.n, p.opts);
          return;
        }
        this.focusStep(n);
      }, 420);
    },
    onGenderUpdate(value) {
      this.form.gender = value;
      this.maybeAutoAdvance();
      this.saveDraft();
    },
    onPreferredGenderUpdate(value) {
      this.form.preferredGender = value;
      this.maybeAutoAdvance();
      this.saveDraft();
    },
    onAgeMinUpdate(value) {
      this.form.ageMin = value;
      this.saveDraft();
    },
    onAgeMaxUpdate(value) {
      this.form.ageMax = value;
      this.saveDraft();
    },
    onPreferencesUpdate(value) {
      this.form.preferences = value;
      this.saveDraft();
    },
    onDobFieldUpdate(field, value) {
      this.form[field] = value;
      this.saveDraft();
    },
    onNameUpdate(value) {
      this.form.name = value;
      this.saveDraft();
    },
    onEmailUpdate(value) {
      this.form.email = value;
      this.saveDraft();
    },
    onLocationIdUpdate(value) {
      this.form.locationId = value;
      this.saveDraft();
    },
    onLocationLabelUpdate(value) {
      this.form.locationLabel = value;
      this.saveDraft();
    },
    maybeAutoAdvance() {
      clearTimeout(this.advanceTimer);
      if (this.current === 1 && isStepValid(1, this.form)) {
        this.advanceTimer = setTimeout(() => {
          if (this.current === 1) {
            this.goTo(2);
          }
        }, 280);
      }
    },
    onLocationPicked() {
      this.saveDraft();
      clearTimeout(this.locationAdvanceTimer);
      this.locationAdvanceTimer = setTimeout(() => {
        if (this.current === 5 && isStepValid(5, this.form)) {
          this.goTo(6);
        }
      }, 250);
    },
    goSocial(provider) {
      window.location.href = oauthUrl(provider);
    },
    startLoaderMessages() {
      let i = 0;
      this.loadTxt = LOADER_MSGS[0];
      clearInterval(this.loaderTimer);
      this.loaderTimer = setInterval(() => {
        i = (i + 1) % LOADER_MSGS.length;
        this.loadTxt = LOADER_MSGS[i];
      }, 2600);
    },
    failSubmit(message) {
      clearInterval(this.loaderTimer);
      this.loadingOn = false;
      this.hideLoaderTimer = setTimeout(() => {
        this.loadingVisible = false;
      }, 260);
      this.submitting = false;
      this.submitError = message || 'Da ist etwas schiefgelaufen — bitte erneut versuchen.';
    },
    async submit() {
      if (!isStepValid(6, this.form) || this.submitting) {
        return;
      }
      this.submitting = true;
      this.submitError = '';
      this.loadingVisible = true;
      requestAnimationFrame(() => {
        this.loadingOn = true;
      });
      this.startLoaderMessages();
      this.clearDraft();

      try {
        await refreshCsrfToken();
        const regResp = await axios.post(zRoute('register'), {
          email: this.form.email.trim(),
          password: this.form.password,
          cid: window.localStorage.getItem('campaign_cid'),
          eid: window.localStorage.getItem('campaign_eid'),
          campaign_query: window.localStorage.getItem('campaign_query'),
        });

        this.$store.dispatch('analytics/gtagEvent', {
          event: 'soi',
          p: { event_category: 'User Registration', event_label: 'SOI Registration' },
        });

        try {
          await axios.put(zRoute('register.update'), {
            name: (this.form.name || '').trim(),
            gender: this.form.gender,
            preferred_gender: this.form.preferredGender,
            birth_date: this.form.year + '-' + pad2(this.form.month) + '-' + pad2(this.form.day),
            location_id: this.form.locationId,
            preferences: this.form.preferences,
            search: { age_from: this.form.ageMin, age_to: this.form.ageMax },
          });
        } catch (e) {
          console.warn('register.update failed, falling back to /home flow:', e);
        }
        const dest = homeUrl();
        const needsEmailVerify = !!(regResp && regResp.data && regResp.data.verification_is_required);
        if (needsEmailVerify) {
          window.location.href = dest;
          return;
        }
        this.loadTxt = 'Profil erstellt ✓ — du wirst weitergeleitet…';
        setTimeout(() => {
          window.location.href = dest;
        }, 400);
      } catch (err) {
        const payload = err && err.response && err.response.data;
        const msg =
          (payload &&
            (payload.message ||
              (payload.errors &&
                ((payload.errors.email && payload.errors.email[0]) ||
                  (payload.errors.password && payload.errors.password[0]))))) ||
          null;
        this.failSubmit(msg);
      }
    },
    saveDraft() {
      clearTimeout(this.draftTimer);
      this.draftTimer = setTimeout(() => {
        if (!consentAllowsDraft()) {
          return;
        }
        try {
          const d = {};
          Object.keys(this.form).forEach((k) => {
            if (k !== 'password' && k !== 'terms') {
              d[k] = this.form[k];
            }
          });
          localStorage.setItem(
            DRAFT_KEY,
            JSON.stringify({ v: 1, ts: Date.now(), step: this.current, data: d }),
          );
        } catch (e) {
          /* ignore quota */
        }
      }, 400);
    },
    clearDraft() {
      try {
        localStorage.removeItem(DRAFT_KEY);
      } catch (e) {
        /* ignore */
      }
    },
    onConsent(e) {
      if (e.detail && e.detail.functional) {
        this.saveDraft();
      } else {
        this.clearDraft();
      }
    },
    restoreDraft() {
      if (!consentAllowsDraft()) {
        return;
      }
      let raw;
      try {
        raw = localStorage.getItem(DRAFT_KEY);
      } catch (e) {
        return;
      }
      if (!raw) {
        return;
      }
      let d;
      try {
        d = JSON.parse(raw);
      } catch (e) {
        return;
      }
      if (!d || d.v !== 1 || !d.ts || Date.now() - d.ts > DRAFT_TTL) {
        this.clearDraft();
        return;
      }
      Object.keys(this.form).forEach((k) => {
        if (k !== 'password' && k !== 'terms' && d.data && k in d.data) {
          this.form[k] = d.data[k];
        }
      });
      if (typeof this.form.locationId === 'string') {
        this.form.locationId = +this.form.locationId || null;
      }
      const target = firstIncompleteStep(this.form);
      if (target > 1) {
        this.goTo(target, { instant: true, silent: true });
        this.showResume = true;
      }
    },
    restart() {
      this.clearDraft();
      location.reload();
    },
  },
};
</script>
